import { useState, useCallback, useRef, useEffect } from 'react'
import { RefreshCw, X } from 'lucide-react'
import { InlineWebhookPanel } from './components/InlineWebhookPanel'
import {
  inputCls, labelCls, SectionLabel, CopyButton, RawJsonCollapsible, ComboSelect,
  EndpointPicker, CurlCollapsible,
} from './components/shared'
import type { EndpointDef } from './components/shared'
import { EMBEDDED_FLOWS, DEFAULT_CREDS } from './data'
import { proxyUrl, buildHeaders, buildMachineHeaders } from './utils'
import type { Creds, LogEntry } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>

type SdkStatus = 'idle' | 'getting-token' | 'initialising' | 'ready' | 'error'

const EMBEDDED_ENDPOINTS: EndpointDef[] = [
  { id: 'machine-session', method: 'POST', label: 'Machine Session', path: '/auth/v2/machine-session', category: 'Auth', core: true },
  { id: 'get-individual', method: 'GET', label: 'Get Individual', path: '/individuals/{entityId}', category: 'Entity Management', core: true },
  // Results
  { id: 'get-results', method: 'GET', label: 'Get Process Results', path: '/individuals/{entityId}/results', category: 'Results' },
  // Entity Management
  { id: 'update-entity', method: 'PATCH', label: 'Update Individual', path: '/individuals/{entityId}', category: 'Entity Management' },
  { id: 'delete-entity', method: 'DELETE', label: 'Delete Individual', path: '/individuals/{entityId}', category: 'Entity Management' },
]

function isEkycFlow(flowType: string): boolean {
  return flowType === 'manual_kyc'
}

// ── Result display ────────────────────────────────────────────────────────────

function WorkflowResultPanel({ wr, fullResponse }: { wr: AnyObj; fullResponse: unknown }) {
  const overall = (wr.workflowExecutionState ?? wr.overallRiskLevel ?? '').toUpperCase()
  const riskLevel = (wr.checkSummary?.overallRiskLevel ?? wr.riskAssessment?.riskLevel ?? overall).toUpperCase()

  function outcomeColor(r: string): { bg: string; border: string; text: string } {
    if (r === 'LOW' || r === 'CLEAR' || r === 'PASS') return { bg: 'rgba(52,211,153,.08)', border: 'rgba(52,211,153,.3)', text: '#34d399' }
    if (r === 'HIGH' || r === 'FAIL') return { bg: 'rgba(248,113,113,.08)', border: 'rgba(248,113,113,.3)', text: '#f87171' }
    if (r === 'MEDIUM' || r === 'REFER' || r === 'REVIEW') return { bg: 'rgba(251,146,60,.08)', border: 'rgba(251,146,60,.3)', text: '#fb923c' }
    return { bg: 'rgba(148,163,184,.08)', border: 'rgba(148,163,184,.3)', text: '#94a3b8' }
  }

  const colors = outcomeColor(riskLevel)

  return (
    <div className="space-y-3 mt-2">
      <div className="rounded-[8px] p-3" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#5b6b85' }}>Outcome</span>
          <span className="text-[11px] font-bold" style={{ color: colors.text }}>{riskLevel || overall || 'DONE'}</span>
        </div>
        {wr.workflowName && (
          <p className="text-[10px] font-mono" style={{ color: '#94a3b8' }}>
            Workflow: <span style={{ color: '#e2e8f0' }}>{wr.workflowName}</span>
          </p>
        )}
        {wr.issues && wr.issues.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {wr.issues.map((issue: any, i: number) => (
              <p key={i} className="text-[10px] font-mono" style={{ color: '#f87171' }}>{typeof issue === 'string' ? issue : JSON.stringify(issue)}</p>
            ))}
          </div>
        )}
      </div>
      <RawJsonCollapsible data={fullResponse} label="Full Response JSON" />
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function EmbeddedWorkspace({ creds, addLog }: { creds: Creds; addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void }) {
  const machineBase = creds.machineBase || DEFAULT_CREDS.machineBase
  const apiBase = creds.apiBase || DEFAULT_CREDS.apiBase
  const hasCreds = !!(creds.customerId && creds.apiKey)

  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('machine-session')
  const [flowType, setFlowType] = useState('idv')
  const [sdkMode, setSdkMode] = useState<'development' | 'dummy'>('development')
  const [customerRef, setCustomerRef] = useState(`ft-${Date.now()}`)
  const [getEntityIdInput, setGetEntityIdInput] = useState('')
  const [sdkStatus, setSdkStatus] = useState<SdkStatus>('idle')
  const [sdkError, setSdkError] = useState<string | null>(null)
  const [events, setEvents] = useState<{ ts: string; eventName: string; args: unknown }[]>([])
  const [sessionInfo, setSessionInfo] = useState<{ token?: string; entityId?: string; expiry?: string } | null>(null)
  const [resultLoading, setResultLoading] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [workflowResult, setWorkflowResult] = useState<any | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const initRef = useRef(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sdkRef = useRef<any>(null)

  // CurlCollapsible URLs
  const machineSessionUrl = `${machineBase}/auth/v2/machine-session`
  const resolvedGetEid = sessionInfo?.entityId ?? getEntityIdInput
  const getIndividualUrl = `${apiBase}/individuals/${resolvedGetEid || '{entityId}'}`

  // Auto-populate GET input from session
  useEffect(() => {
    if (sessionInfo?.entityId && !getEntityIdInput) setGetEntityIdInput(sessionInfo.entityId)
  }, [sessionInfo?.entityId, getEntityIdInput])

  function handleEndpointSelect(id: string) {
    setSelectedEndpoint(id)
  }

  useEffect(() => {
    return () => {
      if (sdkRef.current) { try { sdkRef.current.unmount?.() } catch { /* ignore */ }; sdkRef.current = null }
      initRef.current = false
      const c = document.getElementById('onesdk-container-workspace'); if (c) c.innerHTML = ''
    }
  }, [])

  function appendEvent(eventName: string, args: unknown) {
    setEvents(prev => [...prev, { ts: new Date().toLocaleTimeString(), eventName, args }])
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function mountIdvFlow(instance: any) {
    const container = () => document.getElementById('onesdk-container-workspace')
    const welcome = instance.component('form', { name: 'WELCOME', type: 'ocr' })
    const consent = instance.component('form', { name: 'CONSENT' })
    const idvFlow = instance.flow(flowType)
    welcome.mount(container())
    welcome.on('form:welcome:loaded', () => appendEvent('form:welcome:loaded', {}))
    welcome.on('form:welcome:ready', () => { appendEvent('form:welcome:ready', {}); welcome.unmount(); consent.mount(container()) })
    consent.on('form:consent:loaded', () => appendEvent('form:consent:loaded', {}))
    consent.on('form:consent:ready', () => { appendEvent('form:consent:ready', {}); consent.unmount(); idvFlow.mount(container()) })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    instance.on('detection_complete', (args: any) => { appendEvent('detection_complete', args); instance.individual().submit({ verify: true }).catch(() => {}) })
    instance.on('results', (args: unknown) => appendEvent('results', args))
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function mountEkycFlow(instance: any) {
    const container = () => document.getElementById('onesdk-container-workspace')
    const welcome = instance.component('form', { name: 'WELCOME', type: 'manual' })
    const consent = instance.component('form', { name: 'CONSENT' })
    const personal = instance.component('form', { name: 'PERSONAL' })
    const docComp = instance.component('form', { name: 'DOCUMENT' })
    const review = instance.component('form', { name: 'REVIEW', verify: true })
    welcome.mount(container())
    welcome.on('form:welcome:loaded', () => appendEvent('form:welcome:loaded', {}))
    welcome.on('form:welcome:ready', () => { appendEvent('form:welcome:ready', {}); welcome.unmount(); consent.mount(container()) })
    consent.on('form:consent:ready', () => { appendEvent('form:consent:ready', {}); consent.unmount(); personal.mount(container()) })
    personal.on('form:personal:ready', () => { appendEvent('form:personal:ready', {}); personal.unmount(); docComp.mount(container()) })
    docComp.on('form:document:ready', () => { appendEvent('form:document:ready', {}); docComp.unmount(); review.mount(container()) })
    review.on('form:review:result:success', (a: unknown) => appendEvent('form:review:result:success', a))
    review.on('form:review:result:failed', (a: unknown) => { appendEvent('form:review:result:failed', a) })
    review.on('form:review:result:pending', (a: unknown) => appendEvent('form:review:result:pending', a))
  }

  async function handleStart() {
    if (initRef.current) return
    initRef.current = true

    let token: string | undefined
    let entityId: string | undefined

    if (sdkMode === 'development') {
      setSdkStatus('getting-token')
      const url = `${machineBase}/auth/v2/machine-session`
      const start = Date.now()
      try {
        const res = await fetch('/api/frankie-machine-session', {
          method: 'POST',
          headers: buildMachineHeaders(creds, true),
          body: JSON.stringify({ reference: customerRef }),
        })
        const durationMs = Date.now() - start
        const data = await res.json() as { token?: string; entityId?: string; expiry?: string }
        addLog({ method: 'POST', url, status: res.status, durationMs, requestBody: { reference: customerRef }, responseBody: data, annotation: 'Mint one-time SDK session token — server-side credential exchange' })
        if (!res.ok || !data?.token) { setSdkStatus('error'); setSdkError('Failed to get session token — check credentials'); initRef.current = false; return }
        token = data.token; entityId = data.entityId
        setSessionInfo({ token: data.token, entityId: data.entityId, expiry: data.expiry })
        if (data.entityId) setGetEntityIdInput(data.entityId)
      } catch { setSdkStatus('error'); setSdkError('Session token request failed'); initRef.current = false; return }
    }

    setSdkStatus('initialising')
    try {
      const OneSdkModule = await import('@frankieone/one-sdk')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const OneSdk = (OneSdkModule as any).default
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const instance = await (OneSdk as any)({
        mode: sdkMode,
        ...(sdkMode === 'development' ? { session: { token, appReference: 'dealhub-ft-v1' } } : {}),
        recipe: { form: { provider: { name: 'react', googleApiKey: '' } } },
        devtools: true,
      })
      sdkRef.current = instance
      setSdkStatus('ready')
      const container = document.getElementById('onesdk-container-workspace'); if (container) container.innerHTML = ''
      instance.on('*', ({ eventName, arguments: args }: { eventName: string; arguments: unknown }) => appendEvent(eventName, args))
      if (isEkycFlow(flowType)) mountEkycFlow(instance)
      else mountIdvFlow(instance)
      void entityId
    } catch (e) {
      setSdkStatus('error'); setSdkError(e instanceof Error ? e.message : String(e)); initRef.current = false
    }
  }

  function resetSdk() {
    if (sdkRef.current) { try { sdkRef.current.unmount?.() } catch { /* ignore */ }; sdkRef.current = null }
    initRef.current = false; setSdkStatus('idle'); setSdkError(null); setEvents([]); setSessionInfo(null)
    const c = document.getElementById('onesdk-container-workspace'); if (c) c.innerHTML = ''
    setWorkflowResult(null); setFullResult(null)
  }

  const getResult = useCallback(async () => {
    const eid = sessionInfo?.entityId ?? getEntityIdInput; if (!eid) return
    setResultLoading(true)
    const url = `${apiBase}/individuals/${eid}`
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl(`/individuals/${eid}`), { method: 'GET', headers: buildHeaders(creds) })
      const durationMs = Date.now() - start
      const data = await res.json() as { workflowResult?: unknown; serviceProfiles?: unknown[] }
      addLog({ method: 'GET', url, status: res.status, durationMs, responseBody: data, annotation: 'Fetch individual record after SDK verification' })
      if (res.ok) {
        setFullResult(data)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setWorkflowResult((data as any)?.workflowResult ?? (data as any)?.serviceProfiles?.[0]?.workflowSummaries?.[0] ?? null)
      }
    } catch { /* logged */ } finally { setResultLoading(false) }
  }, [sessionInfo?.entityId, getEntityIdInput, apiBase, creds, addLog])

  const statusDotCls: Record<SdkStatus, string> = {
    idle: '#6b7280', 'getting-token': '#fbbf24', initialising: '#fbbf24', ready: '#34d399', error: '#f87171',
  }
  const statusLabel: Record<SdkStatus, string> = {
    idle: 'Idle', 'getting-token': 'Getting token…', initialising: 'Initialising…', ready: 'Ready', error: 'Error',
  }

  const isAnimating = sdkStatus === 'getting-token' || sdkStatus === 'initialising'

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: '200px minmax(350px, 400px) 1fr', alignItems: 'start', padding: '18px 0' }}
    >
      {/* ── COLUMN 1: Endpoint Picker ── */}
      <EndpointPicker
        endpoints={EMBEDDED_ENDPOINTS}
        selectedId={selectedEndpoint}
        onSelect={handleEndpointSelect}
      />

      {/* ── COLUMN 2: Form Panel ── */}
      <div className="space-y-3">
        {/* machine-session form */}
        {selectedEndpoint === 'machine-session' && (
          <div className="rounded-[14px] overflow-hidden" style={{ background: '#111c30', border: '1px solid #1e2c44' }}>
            {/* Header strip */}
            <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono" style={{ background: 'rgba(52,211,153,.13)', color: '#34d399' }}>POST</span>
              <span className="text-[11px] font-mono text-[#8aa0c2] truncate flex-1">/auth/v2/machine-session</span>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold" style={{ color: '#e2e8f0' }}>SDK Configuration</p>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: statusDotCls[sdkStatus], ...(isAnimating ? { animation: 'pulse 1.5s infinite' } : {}) }} />
                  <span className="text-[10px]" style={{ color: '#94a3b8' }}>{statusLabel[sdkStatus]}</span>
                </div>
              </div>

              <div>
                <label className={labelCls}>SDK Flow</label>
                <ComboSelect
                  value={flowType}
                  onChange={setFlowType}
                  options={EMBEDDED_FLOWS.map(f => ({ value: f.id, label: f.label }))}
                  placeholder="e.g. idv, ocr_only, or custom flow"
                  className={inputCls}
                />
                <p className="mt-1 text-[9px]" style={{ color: '#5b6b85' }}>
                  {EMBEDDED_FLOWS.find(f => f.id === flowType)?.note}
                </p>
              </div>

              <div>
                <label className={labelCls}>SDK Mode</label>
                <select value={sdkMode} onChange={e => setSdkMode(e.target.value as 'development' | 'dummy')} className={inputCls}>
                  <option value="development">development — real session token</option>
                  <option value="dummy">dummy — no credentials needed</option>
                </select>
              </div>

              {sdkMode === 'development' && (
                <div>
                  <label className={labelCls}>Customer Reference</label>
                  <input value={customerRef} onChange={e => setCustomerRef(e.target.value)} className={inputCls} placeholder="ft-unique-ref" />
                </div>
              )}

              {/* Mounting sequence info */}
              <div className="rounded-[7px] p-2.5" style={{ background: '#0a1322', border: '1px solid #1e2c44' }}>
                <SectionLabel>Component mounting sequence</SectionLabel>
                <div className="flex items-center gap-1 flex-wrap">
                  {isEkycFlow(flowType) ? (
                    <>
                      {['WELCOME', 'CONSENT', 'PERSONAL', 'DOCUMENT'].map((c, i) => (
                        <span key={c}>
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold" style={{ background: 'rgba(139,92,246,.15)', color: '#a78bfa' }}>{c}</span>
                          {i < 3 && <span className="text-[9px]" style={{ color: '#5b6b85' }}> → </span>}
                        </span>
                      ))}
                      <span className="text-[9px]" style={{ color: '#5b6b85' }}> → </span>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold" style={{ background: 'rgba(251,146,60,.15)', color: '#fb923c' }}>REVIEW (verify)</span>
                    </>
                  ) : (
                    <>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold" style={{ background: 'rgba(139,92,246,.15)', color: '#a78bfa' }}>WELCOME</span>
                      <span className="text-[9px]" style={{ color: '#5b6b85' }}> → </span>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold" style={{ background: 'rgba(139,92,246,.15)', color: '#a78bfa' }}>CONSENT</span>
                      <span className="text-[9px]" style={{ color: '#5b6b85' }}> → </span>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold" style={{ background: 'rgba(52,211,153,.15)', color: '#34d399' }}>flow('{flowType}')</span>
                      <span className="text-[9px]" style={{ color: '#5b6b85' }}> → </span>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold" style={{ background: 'rgba(251,146,60,.15)', color: '#fb923c' }}>detection_complete</span>
                      <span className="text-[9px]" style={{ color: '#5b6b85' }}> → submit()</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleStart}
                  disabled={sdkStatus !== 'idle' || (!hasCreds && sdkMode === 'development')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[11px] font-semibold transition-opacity disabled:opacity-40"
                  style={{ background: '#6366f1', color: '#fff' }}
                >
                  {isAnimating && <RefreshCw size={11} className="animate-spin" />}
                  {sdkStatus === 'idle' ? (sdkMode === 'dummy' ? 'Start SDK (dummy)' : 'Get Token & Start SDK') : statusLabel[sdkStatus]}
                </button>
                <button
                  onClick={resetSdk}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[11px] font-semibold"
                  style={{ background: '#1e2c44', color: '#94a3b8' }}
                >
                  Reset
                </button>
              </div>

              {sdkError && (
                <p className="text-[10px] px-2 py-1.5 rounded-[7px]" style={{ color: '#f87171', background: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.3)' }}>
                  {sdkError}
                </p>
              )}

              {sessionInfo?.entityId && (
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-[7px]" style={{ background: 'rgba(52,211,153,.08)', border: '1px solid rgba(52,211,153,.25)' }}>
                  <span className="text-[10px] font-semibold" style={{ color: '#34d399' }}>Token received</span>
                  <span className="text-[9px]" style={{ color: '#5b6b85' }}>entityId:</span>
                  <code className="text-[10px] font-mono" style={{ color: '#34d399' }}>{sessionInfo.entityId}</code>
                  <CopyButton text={sessionInfo.entityId} />
                </div>
              )}

              {sdkMode === 'dummy' && sdkStatus === 'idle' && (
                <p className="text-[10px] px-2 py-1.5 rounded-[7px]" style={{ color: '#60a5fa', background: 'rgba(96,165,250,.08)', border: '1px solid rgba(96,165,250,.25)' }}>
                  dummy mode — SDK uses built-in test data, no credentials required.
                </p>
              )}
              {sdkMode === 'development' && !hasCreds && (
                <p className="text-[10px]" style={{ color: '#fb923c' }}>Add credentials above to use development mode.</p>
              )}
            </div>
          </div>
        )}

        {/* get-individual form */}
        {selectedEndpoint === 'get-individual' && (
          <div className="rounded-[14px] overflow-hidden" style={{ background: '#111c30', border: '1px solid #1e2c44' }}>
            {/* Header strip */}
            <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono" style={{ background: 'rgba(96,165,250,.15)', color: '#60a5fa' }}>GET</span>
              <span className="text-[11px] font-mono text-[#8aa0c2] truncate flex-1">/individuals/{'{entityId}'}</span>
            </div>

            <div className="p-4 space-y-4">
              <p className="text-[10px]" style={{ color: '#5b6b85' }}>
                After verification completes, fetch the individual entity for the full workflowResult.
              </p>

              <div>
                <label className={labelCls}>Entity ID</label>
                <input
                  className={inputCls}
                  value={getEntityIdInput}
                  onChange={e => setGetEntityIdInput(e.target.value)}
                  placeholder="entityId from machine session"
                />
              </div>

              <div className="space-y-2">
                {!hasCreds && <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>}
                <button
                  onClick={() => void getResult()}
                  disabled={resultLoading || !(sessionInfo?.entityId ?? getEntityIdInput)}
                  className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                  style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
                >
                  {resultLoading
                    ? <span className="flex items-center justify-center gap-2"><RefreshCw size={14} className="animate-spin" />Loading…</span>
                    : 'Get Result'
                  }
                </button>
                {!(sessionInfo?.entityId ?? getEntityIdInput) && (
                  <span className="text-[10px] italic" style={{ color: '#5b6b85' }}>Start Machine Session first to get entityId</span>
                )}
              </div>

              {workflowResult ? (
                <WorkflowResultPanel wr={workflowResult as AnyObj} fullResponse={fullResult} />
              ) : (
                <p className="text-[10px] italic" style={{ color: '#5b6b85' }}>No results yet — complete verification and click Get Result</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── COLUMN 3: SDK container + events (always visible) ── */}
      <div className="space-y-3" style={{ minWidth: 0 }}>
        {/* cURL — above webhooks */}
        {selectedEndpoint === 'machine-session' && sdkMode === 'development' ? (
          <CurlCollapsible method="POST" url={machineSessionUrl} creds={creds} body={{ reference: customerRef }} />
        ) : selectedEndpoint === 'get-individual' ? (
          <CurlCollapsible method="GET" url={getIndividualUrl} creds={creds} />
        ) : null}

        <InlineWebhookPanel
          entityId={sessionInfo?.entityId ?? null}
          customerId={creds.customerId ?? null}
        />

        {/* SDK Verification */}
        <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #1e2c44' }}>
          <div className="px-3 py-2" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#5b6b85' }}>SDK Verification</p>
          </div>

          <div className="flex gap-0" style={{ background: '#111c30' }}>
            {/* SDK container */}
            <div className="flex-1 min-w-0">
              <div className="px-3 py-2" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44', borderRight: '1px solid #1e2c44' }}>
                <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#5b6b85' }}>SDK Container</span>
              </div>
              <div style={{ minHeight: 400, background: '#0a1322', position: 'relative' }}>
                <div id="onesdk-container-workspace" className="w-full" style={{ minHeight: 400 }} />
                {sdkStatus === 'idle' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-[11px] italic" style={{ color: '#5b6b85' }}>Click "Get Token & Start SDK" to launch the embedded OneSDK</p>
                  </div>
                )}
              </div>
            </div>

            {/* Events log */}
            <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #1e2c44' }}>
              <div className="flex items-center justify-between px-3 py-2" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#5b6b85' }}>SDK Events</span>
                  {events.length > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-mono" style={{ background: 'rgba(139,92,246,.2)', color: '#a78bfa' }}>
                      {events.length}
                    </span>
                  )}
                </div>
                {events.length > 0 && (
                  <button onClick={() => setEvents([])} style={{ color: '#5b6b85', fontSize: 10 }}>
                    <X size={10} />
                  </button>
                )}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', maxHeight: 460 }}>
                {events.length === 0 ? (
                  <p className="text-[10px] italic p-3" style={{ color: '#5b6b85' }}>No events yet</p>
                ) : (
                  events.map((ev, i) => (
                    <div key={i} className="px-3 py-2" style={{ borderBottom: '1px solid #1e2c44' }}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="font-mono text-[9px]" style={{ color: '#5b6b85' }}>{ev.ts}</span>
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-mono truncate" style={{ background: 'rgba(139,92,246,.2)', color: '#a78bfa' }}>{ev.eventName}</span>
                      </div>
                      {ev.args !== undefined && (
                        <pre className="text-[9px] font-mono overflow-auto max-h-20" style={{ color: '#94a3b8' }}>
                          {JSON.stringify(ev.args, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
